import { useMemo, useState } from "react";
import { Check, Sparkles, Briefcase, BrushCleaning, Home, AlertTriangle, CheckCircle2, XCircle, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { STATUS_COLORS, BRAND_COLORS, effectiveBookingStatus, withAlpha } from "../lib/bookingStatus";
import { deDateShort } from "../lib/dateFormat";

/**
 * Auto-derived 3-milestone timeline for a booking's preparation progress.
 *
 * Feb 2026 · KRITISCHER FIX Timeline-Status-Logik
 * ----------------------------------------------
 * Der SERVER berechnet den Snapshot in `_compute_timeline_snapshot` und
 * hängt ihn als `booking.timeline_stages` an. Damit sind Timeline UND
 * Kalender IMMER auf derselben Wahrheit:
 *   - Abfahrt      : Kunden-Abreise-Checkliste 100 % abgehakt
 *   - Vorbereitung : ALLE gebuchten Services (Standard + Extras) erledigt
 *   - Ankunft      : Vorbereitung done + alle Live-Status-Kacheln OK +
 *                    Anreise-Datum erreicht
 *
 * Wird ein Haken entfernt, springt die Phase beim nächsten Fetch zurück.
 *
 * `computeStages` fällt auf die Legacy-Client-Berechnung nur zurück, wenn
 * KEIN `booking.timeline_stages` vorhanden ist (z. B. Design-Gallery-Mocks
 * oder falls das Backend die Migration noch nicht ausgerollt hat).
 */
const STAGE_DEFS = [
  { key: "reinigung",    label_de: "Abfahrt",       label_en: "Departure",   snapKey: "abfahrt",      Icon: Briefcase,     legacyKeys: ["arrival_cleaning"],                offsetDays: -2 },
  { key: "vorbereitung", label_de: "Vorbereitung",  label_en: "Preparation", snapKey: "vorbereitung", Icon: BrushCleaning, legacyKeys: ["linen_change", "plants", "airing"], offsetDays: -1 },
  { key: "ankunft",      label_de: "Ankunft",       label_en: "Arrival",     snapKey: "ankunft",      Icon: Home,          legacyKeys: ["weekly_checks"],                    offsetDays:  0 },
];

const DE_DATE_SHORT = (iso) => {
  if (!iso) return "";
  try {
    return deDateShort(iso);
  } catch { return iso; }
};

function shiftIso(iso, deltaDays) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + deltaDays);
    return d.toISOString().slice(0, 10);
  } catch { return ""; }
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/** Legacy-Fallback (nur wenn Server keinen Snapshot liefert). Wird von
 *  TeamDesignGallery-Mocks noch verwendet. Behält das alte, hartcodierte
 *  Verhalten für Rückwärtskompatibilität. */
function legacyComputeStages(booking) {
  const completed = new Set(booking?.completed_services || []);
  const disabled  = new Set(booking?.disabled_standard_services || []);
  const isDoneOrOptOut = (k) => completed.has(k) || disabled.has(k);
  const isDone         = (k) => completed.has(k);
  const toDate = booking?.to_date || null;

  const stages = STAGE_DEFS.map(({ key, label_de, label_en, legacyKeys, offsetDays, Icon }) => {
    const totalNeeded = legacyKeys.filter((k) => !disabled.has(k)).length;
    const doneCount   = legacyKeys.filter(isDone).length;
    return {
      key,
      label_de,
      label_en,
      Icon,
      done: legacyKeys.every(isDoneOrOptOut),
      expectedDate: toDate ? shiftIso(toDate, offsetDays) : null,
      progress: { done: doneCount, total: totalNeeded },
    };
  });
  stages[2].done = stages[2].done && stages[0].done && stages[1].done;
  const currentIndex = stages.findIndex((s) => !s.done);
  return { stages, currentIndex: currentIndex === -1 ? stages.length : currentIndex };
}

export function computeStages(booking) {
  const snap = booking?.timeline_stages;
  if (snap && snap.abfahrt && snap.vorbereitung && snap.ankunft) {
    const toDate = booking?.to_date || null;
    const stages = STAGE_DEFS.map(({ key, label_de, label_en, snapKey, offsetDays, Icon }) => {
      const s = snap[snapKey] || {};
      const progress = s.progress || { done: 0, total: 0 };
      return {
        key,
        label_de,
        label_en,
        Icon,
        done: !!s.done,
        expectedDate: s.expected_date || (toDate ? shiftIso(toDate, offsetDays) : null),
        progress,
        requires: s.requires || null,
      };
    });
    const currentIndex = stages.findIndex((s) => !s.done);
    return { stages, currentIndex: currentIndex === -1 ? stages.length : currentIndex };
  }
  return legacyComputeStages(booking);
}

/**
 * Bestimme die Status-Farbe eines Timeline-Kreises anhand des systemweiten
 * 4-Farb-Schemas. Prüfreihenfolge:
 *   1. GRÜN   — Schritt komplett erledigt.
 *   2. ROT    — Verzögerung: erwartetes Datum verstrichen und Schritt nicht
 *               erledigt, ODER Buchung insgesamt überfällig, ODER das
 *               verknüpfte Objekt hat `status === "attention"` (kritischer
 *               Live-Status → jede offene Phase signalisiert Handlungsbedarf).
 *   3. BLAU   — Aktueller aktiver Schritt einer laufenden Buchung.
 *   4. GRAU   — Noch nicht gestartet (zukünftige Schritte, angefragte oder
 *               stornierte Buchungen).
 */
function stageStatus(stage, idx, currentIndex, booking, effStatus, today, property) {
  if (stage.done) return { color: STATUS_COLORS.green, key: "done" };

  if (effStatus === "cancelled") return { color: STATUS_COLORS.grey, key: "cancelled" };

  // Feb 2026 · HARDCODED-RULE Ankunft-Latch: die Ankunft-Phase ist RED-only —
  // sie bleibt ROT solange auch nur eine der drei Bedingungen (Vorbereitung
  // erledigt · Zusatzleistungen erledigt · Live-Status ok) offen ist. Kein
  // GRAU/BLAU-Zwischenstadium für Ankunft.
  if (stage.key === "ankunft") {
    return { color: STATUS_COLORS.red, key: "problem" };
  }

  if (property && property.status === "attention") {
    return { color: STATUS_COLORS.red, key: "problem" };
  }

  const expected = stage.expectedDate;
  const stageOverdue = expected && expected < today;
  if (stageOverdue || effStatus === "overdue") {
    return { color: STATUS_COLORS.red, key: "problem" };
  }

  const isActive = effStatus === "in_service" || effStatus === "in_progress"
    || effStatus === "confirmed" || effStatus === "accepted";
  if (idx === currentIndex && isActive) {
    return { color: STATUS_COLORS.blue, key: "in_progress" };
  }

  return { color: STATUS_COLORS.grey, key: "pending" };
}

function stageProgressLabel(stage, de) {
  if (stage.key === "reinigung") {
    if (stage.progress.total === 0) return de ? "keine Punkte" : "no items";
    return `${stage.progress.done} / ${stage.progress.total} ${de ? "Punkte" : "items"}`;
  }
  if (stage.key === "vorbereitung") {
    if (stage.progress.total === 0) return de ? "keine Services" : "no services";
    return `${stage.progress.done} / ${stage.progress.total} ${de ? "Services" : "services"}`;
  }
  return `${stage.progress.done} / ${stage.progress.total} ${de ? "Bedingungen" : "conditions"}`;
}

function stageTooltip(stage, status, de) {
  if (status.key === "done") return de ? "Erledigt" : "Done";
  if (status.key === "problem") {
    if (stage.key === "ankunft" && stage.requires) {
      const missing = [];
      if (!stage.requires.prep_done) missing.push(de ? "Leistungen dieser Abwesenheit offen" : "Services for this booking pending");
      if (!stage.requires.zusatz_done) missing.push(de ? "Anfragen offen" : "Requests open");
      if (!stage.requires.tiles_ok) missing.push(de ? "Live-Status nicht bereit" : "Live status not ready");
      if (missing.length) return (de ? "Klick für Details · " : "Click for details · ") + missing.join(" · ");
    }
    return de ? "Verzögerung: erwartetes Datum verstrichen" : "Delay: expected date has passed";
  }
  if (status.key === "in_progress") return de ? "In Bearbeitung" : "In progress";
  return de ? "Noch nicht gestartet" : "Not started yet";
}

const LATCH_ROWS_DE = [
  { key: "prep_done", label: "Alle Leistungen dieser Abwesenheit erledigt" },
  { key: "zusatz_done", label: "Alle Anfragen erledigt" },
  { key: "tiles_ok", label: "Live-Status auf „In Ordnung“" },
];
const LATCH_ROWS_EN = [
  { key: "prep_done", label: "All services for this booking done" },
  { key: "zusatz_done", label: "All requests done" },
  { key: "tiles_ok", label: "Live status \u201Call fine\u201D" },
];

function LatchExplainPopover({ requires, onClose, testidBase }) {
  const { lang } = useI18n();
  const de = lang !== "en";
  const LATCH_ROWS = de ? LATCH_ROWS_DE : LATCH_ROWS_EN;
  return (
    <div
      role="dialog"
      aria-label={de ? "Latch-Erklärung Ankunft" : "Arrival latch explanation"}
      data-testid={testidBase}
      className="absolute z-30 mt-2 left-0 right-0 md:right-auto md:min-w-[280px] max-w-[340px]"
    >
      <div
        className="bg-white shadow-lg p-4 relative"
        style={{
          border: `1px solid ${withAlpha(STATUS_COLORS.red, 0.35)}`,
          boxShadow: `0 6px 24px ${withAlpha(BRAND_COLORS.navy, 0.18)}`,
        }}
      >
        <div className="flex items-start gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2} style={{ color: STATUS_COLORS.red }} />
          <div className="flex-1">
            <div className="label-caps text-navy" style={{ color: STATUS_COLORS.red }}>
              {de ? "Ankunft blockiert" : "Arrival blocked"}
            </div>
            <div className="text-[11.5px] text-navy-dark/70 mt-0.5 leading-snug">
              {de
                ? "Diese drei Bedingungen müssen erfüllt sein, bevor die Ankunft-Phase auf Grün springt:"
                : "All three conditions must be met before the arrival phase turns green:"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={de ? "Schließen" : "Close"}
            data-testid={`${testidBase}-close`}
            className="shrink-0 p-1 -m-1 text-navy-dark/40 hover:text-navy transition-colors"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
        <ul className="space-y-1.5">
          {LATCH_ROWS.map((row) => {
            const ok = Boolean(requires && requires[row.key]);
            const rowColor = ok ? STATUS_COLORS.green : STATUS_COLORS.red;
            return (
              <li
                key={row.key}
                className="flex items-start gap-2 text-[12px] leading-snug"
                data-testid={`${testidBase}-row-${row.key}`}
                data-ok={ok ? "true" : "false"}
              >
                {ok ? (
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.2} style={{ color: rowColor }} />
                ) : (
                  <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.2} style={{ color: rowColor }} />
                )}
                <span
                  className="text-navy-dark/85"
                  style={ok ? { textDecoration: "line-through", textDecorationColor: withAlpha(BRAND_COLORS.navy, 0.2), color: withAlpha(BRAND_COLORS.navy, 0.55) } : {}}
                >
                  {row.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default function BookingProgressTimeline({ booking, property = null, compact = false, variant = "team", dateAware = false }) {
  const { lang } = useI18n();
  // Team mode remains German always; customer follows language toggle.
  const de = variant === "team" || lang !== "en";
  // `dateAware` (Kundenansicht): ein Schritt darf NICHT als erledigt gelten,
  // solange sein erwartetes Datum in der Zukunft liegt — auch wenn im Backend
  // die zugehörigen Checklisten bereits abgehakt sind.
  const { stages, currentIndex } = useMemo(() => {
    const base = computeStages(booking);
    if (!dateAware) return base;
    const t = todayIso();
    const guarded = base.stages.map((s) => ({
      ...s,
      done: s.done && !(s.expectedDate && s.expectedDate > t),
    }));
    const ci = guarded.findIndex((s) => !s.done);
    return { stages: guarded, currentIndex: ci === -1 ? guarded.length : ci };
  }, [booking, dateAware]);
  const allDone = currentIndex === stages.length;
  const today   = useMemo(() => todayIso(), []);
  const effStatus = useMemo(() => effectiveBookingStatus(booking) || "requested", [booking]);
  const [openStageKey, setOpenStageKey] = useState(null);

  const isCustomer = variant === "customer";
  const paddingCls = compact ? (isCustomer ? "px-3 py-2.5" : "p-4") : "p-5 md:p-6";
  const rootTestId = isCustomer
    ? "booking-progress-timeline-customer"
    : "booking-progress-timeline";

  const headerLabel = isCustomer
    ? (allDone ? (de ? "Ihr Zuhause ist bereit" : "Your home is ready") : (de ? "Ihre Anreise wird vorbereitet" : "Your return is being prepared"))
    : "Vorbereitungs-Fortschritt";

  const circleSize = compact ? "w-7 h-7" : "w-8 h-8";
  const iconSize   = compact ? "w-3.5 h-3.5" : "w-4 h-4";

  return (
    <div
      className={`bg-white border border-navy/10 ${paddingCls}`}
      data-testid={rootTestId}
    >
      <div className="flex items-baseline gap-2 mb-3">
        <Sparkles className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
        <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{headerLabel}</div>
        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 ml-auto">
          {allDone
            ? (de ? "Alles bereit" : "All ready")
            : (de ? `Schritt ${currentIndex + 1} von ${stages.length}` : `Step ${currentIndex + 1} of ${stages.length}`)}
        </div>
      </div>
      <ol
        className={`flex items-start ${compact ? "gap-2" : "gap-2 md:gap-4"}`}
        aria-label={de ? "Zeitstrahl der Vorbereitung" : "Preparation timeline"}
      >
        {stages.map((s, idx) => {
          const status = stageStatus(s, idx, currentIndex, booking, effStatus, today, property);
          const isCurrent = status.key === "in_progress";
          const isDone    = status.key === "done";
          const isProblem = status.key === "problem";
          const IconComp  = s.Icon;

          const filled = isDone || isProblem;
          const circleStyle = filled
            ? { background: status.color, borderColor: status.color, color: BRAND_COLORS.ivory }
            : {
                background: `${status.color}14`,
                borderColor: status.color,
                color: status.color,
              };
          const iconColor = filled ? BRAND_COLORS.ivory : status.color;

          const connectorColor = s.done ? STATUS_COLORS.green : withAlpha(BRAND_COLORS.navy, 0.14);
          const connectorAnim  = isCurrent && !s.done ? "animate-pulse" : "";
          const canExplain = isProblem && s.key === "ankunft" && s.requires;
          const isOpen     = openStageKey === s.key;

          return (
            <li key={s.key} className="flex-1 min-w-0 relative" data-testid={`timeline-stage-${s.key}`}>
              <div className="flex items-center gap-2">
                <span
                  className={`shrink-0 flex items-center justify-center border-2 rounded-full transition-colors ${circleSize} ${
                    isCurrent ? "ring-4" : ""
                  } ${canExplain ? "cursor-help" : ""}`}
                  style={{
                    ...circleStyle,
                    ...(isCurrent ? { "--tw-ring-color": `${status.color}33` } : {}),
                    boxShadow: isCurrent ? `0 0 0 4px ${status.color}22` : "none",
                  }}
                  aria-current={isCurrent ? "step" : undefined}
                  aria-expanded={canExplain ? isOpen : undefined}
                  aria-haspopup={canExplain ? "dialog" : undefined}
                  role={canExplain ? "button" : undefined}
                  tabIndex={canExplain ? 0 : undefined}
                  data-testid={`timeline-dot-${s.key}-${status.key}`}
                  data-status={status.key}
                  title={stageTooltip(s, status, de)}
                  onClick={canExplain ? (e) => { e.stopPropagation(); setOpenStageKey(isOpen ? null : s.key); } : undefined}
                  onKeyDown={canExplain ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenStageKey(isOpen ? null : s.key);
                    }
                    if (e.key === "Escape") setOpenStageKey(null);
                  } : undefined}
                >
                  {isDone ? (
                    <Check className={iconSize} strokeWidth={3} style={{ color: iconColor }} />
                  ) : isProblem ? (
                    <AlertTriangle className={iconSize} strokeWidth={2.4} style={{ color: iconColor }} />
                  ) : (
                    IconComp && <IconComp className={iconSize} strokeWidth={1.9} style={{ color: iconColor }} />
                  )}
                </span>
                {idx < stages.length - 1 && (
                  <span
                    className={`flex-1 h-[2px] rounded-full ${connectorAnim}`}
                    style={{ background: connectorColor }}
                    aria-hidden="true"
                    data-testid={`timeline-connector-${s.key}`}
                    data-connector-color={s.done ? "green" : "grey"}
                  />
                )}
              </div>
              <div className="mt-2">
                <div
                  className={`${compact ? "text-[11.5px]" : "text-[12.5px]"} leading-tight font-medium`}
                  style={{ color: status.color === STATUS_COLORS.grey ? "#6C7480" : status.color }}
                >
                  {de ? s.label_de : s.label_en}
                </div>
                {s.expectedDate && (
                  <div
                    className={`text-[10.5px] font-medium mt-0.5 ${
                      isDone ? "text-navy-dark/55" : isProblem ? "" : isCurrent ? "text-navy" : "text-navy-dark/60"
                    }`}
                    style={{
                      ...(isProblem ? { color: STATUS_COLORS.red } : {}),
                      ...(isDone ? { textDecoration: "line-through", textDecorationColor: withAlpha(BRAND_COLORS.navy, 0.2) } : {}),
                    }}
                    data-testid={`timeline-date-${s.key}`}
                  >
                    {s.key === "ankunft" ? (de ? "am " : "on ") : (de ? "bis " : "by ")}
                    {DE_DATE_SHORT(s.expectedDate)}
                  </div>
                )}
                <div className="text-[10px] tracking-widest uppercase text-navy-dark/45 mt-0.5">
                  {isDone
                    ? (de ? "erledigt" : "done")
                    : isProblem
                    ? (de ? "Verzögerung" : "Delay")
                    : isCurrent
                    ? (de ? "In Bearbeitung" : "In progress")
                    : stageProgressLabel(s, de)}
                </div>
              </div>
              {canExplain && isOpen && (
                <LatchExplainPopover
                  requires={s.requires}
                  onClose={() => setOpenStageKey(null)}
                  testidBase={`timeline-latch-explain-${s.key}`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
